// Admin console auth — simpler than the org-tier's AuthContext.
// No orgSlug, no impersonation start/end (admin USES impersonation but is
// never itself impersonated), no admin_console_required error branch.
// Just: exchange CF Access JWT → session JWT → hydrate /me.

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, setSessionToken } from '../lib/api.js';

const AuthContext = createContext(null);
const REFRESH_INTERVAL_MS = 13 * 60 * 1000; // 13 minutes — refresh before 15-min TTL

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, loading: true, error: null });
  const refreshTimer = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const session = await api.post('/auth/session/refresh');
      setSessionToken(session.session_token);
      const me = await api.get('/me');
      const user = me.data || me;
      setState({ user, loading: false, error: null });
      clearRefreshTimer();
      refreshTimer.current = setTimeout(refreshSession, REFRESH_INTERVAL_MS);
    } catch {
      setSessionToken(null);
      setState({ user: null, loading: false, error: 'Session expired' });
    }
  }, [clearRefreshTimer]);

  const initSession = useCallback(async () => {
    try {
      const session = await api.post('/auth/session');
      setSessionToken(session.session_token);
      const me = await api.get('/me');
      const user = me.data || me;
      setState({ user, loading: false, error: null });
      clearRefreshTimer();
      refreshTimer.current = setTimeout(refreshSession, REFRESH_INTERVAL_MS);
    } catch (err) {
      setSessionToken(null);
      if (err.status === 403) {
        setState({
          user: null,
          loading: false,
          error: err.message || 'Access restricted to Colorado Communications administrators',
        });
      } else if (err.status === 401) {
        // Auth failed — surface the underlying error so it's visible in the
        // UI. Auto-reload disabled to avoid loops when the failure is
        // configuration (e.g. CF_ACCESS_AUD mismatch) rather than expired.
        console.error('CF Access verification failed:', err.status, err.body);
        setState({
          user: null,
          loading: false,
          error: `Authentication failed (${err.status}): ${err.body?.error || err.message}`,
        });
      } else {
        setState({
          user: null,
          loading: false,
          error: err.message || 'Unable to connect to Verso',
        });
      }
    }
  }, [clearRefreshTimer, refreshSession]);

  useEffect(() => {
    initSession();
    return () => clearRefreshTimer();
  }, [initSession, clearRefreshTimer]);

  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.user,
    refetch: initSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
